import React, { useState, useEffect } from 'react';
import { Loader2, ArrowLeft, Copy, ExternalLink, XCircle, CheckCircle, Clock } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface LineItem {
  description: string;
  amount: number;
  quantity: number;
}

export interface PaymentLink {
  id: string;
  token: string;
  title: string;
  notes?: string;
  total_amount: number;
  status: string;
  expires_at?: string;
  created_at: string;
  line_items: LineItem[];
  shareUrl: string;
}

interface Transaction {
  id: string;
  phone_number: string;
  amount: number;
  status: string;
  mpesa_receipt: string | null;
  gateway: string;
  created_at: string;
}

interface LinkHistoryResponse {
  link: PaymentLink;
  transactions: Transaction[];
}

interface PaymentLinkDetailsPaneProps {
  businessId: string;
  token: string;
  onClose: () => void;
}

export const PaymentLinkDetailsPane: React.FC<PaymentLinkDetailsPaneProps> = ({
  businessId,
  token,
  onClose
}) => {
  const [data, setData] = useState<LinkHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data: res } = await apiClient.get(`/payment-links/${token}/history`);
        setData(res);
      } catch (error: any) {
        console.error('Failed to fetch link history:', error);
        toast.error('Error', 'Failed to load payment link history');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [token, toast]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied', 'Copied to clipboard!');
    } catch (err) {
      toast.error('Error', 'Failed to copy');
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full flex items-center justify-center p-8 bg-light-card dark:bg-dark-card border-l border-light-border dark:border-dark-border">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h-full w-full p-6 bg-light-card dark:bg-dark-card border-l border-light-border dark:border-dark-border">
        <button onClick={onClose} className="p-2 mb-4 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg text-light-text dark:text-dark-text transition-colors">
          <ArrowLeft size={24} />
        </button>
        <div className="text-center text-light-subtle dark:text-dark-subtle mt-10">
          Payment link details could not be loaded.
        </div>
      </div>
    );
  }

  const { link, transactions } = data;
  const publicUrl = `https://majengo.co.ke/pay/${link.token}`;

  return (
    <div className="h-full w-full flex flex-col bg-light-card dark:bg-dark-card border-l border-light-border dark:border-dark-border">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-light-border dark:border-dark-border flex items-center gap-3">
        <button onClick={onClose} className="p-2 -ml-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg text-light-text dark:text-dark-text transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Link Details</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Link Info Card */}
        <div className="bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-xl p-5 mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-light-text dark:text-dark-text">{link.title}</h3>
              <p className="text-light-subtle dark:text-dark-subtle text-sm mt-1">KES {link.total_amount.toLocaleString()}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
              link.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
              link.status === 'expired' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
              link.status === 'cancelled' ? 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400' :
              'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'
            }`}>
              {link.status}
            </span>
          </div>
          
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => copyToClipboard(publicUrl)}
              className="flex-1 flex items-center justify-center gap-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border py-2 rounded-lg text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-border dark:hover:border-dark-border transition"
            >
              <Copy size={16} /> Copy URL
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 flex items-center justify-center bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:bg-light-border dark:hover:border-dark-border transition text-light-text dark:text-dark-text"
              title="Open Link"
            >
              <ExternalLink size={18} />
            </a>
          </div>
        </div>

        {/* Transaction History */}
        <h3 className="text-sm font-bold uppercase tracking-wider text-light-subtle dark:text-dark-subtle mb-4">
          Payment Attempts ({transactions.length})
        </h3>

        {transactions.length === 0 ? (
          <div className="text-center py-10 text-light-subtle dark:text-dark-subtle bg-light-bg dark:bg-dark-bg rounded-xl border border-dashed border-light-border dark:border-dark-border">
            No payment attempts yet.
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map(txn => (
              <div key={txn.id} className="p-4 rounded-xl border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg flex items-start gap-4">
                <div className="mt-1">
                  {txn.status === 'paid' ? (
                    <CheckCircle className="text-green-500" size={20} />
                  ) : txn.status === 'failed' ? (
                    <XCircle className="text-red-500" size={20} />
                  ) : (
                    <Clock className="text-yellow-500" size={20} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <p className="font-bold text-light-text dark:text-dark-text text-sm">
                      {txn.phone_number || 'Unknown Number'}
                    </p>
                    <span className="text-xs font-bold text-light-text dark:text-dark-text">
                      KES {txn.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-light-subtle dark:text-dark-subtle capitalize">
                      {txn.status} • {txn.gateway}
                    </p>
                    <p className="text-[10px] text-light-subtle dark:text-dark-subtle">
                      {new Date(txn.created_at).toLocaleString()}
                    </p>
                  </div>
                  {txn.mpesa_receipt && (
                    <div className="mt-2 inline-block px-2 py-1 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded text-xs font-bold text-primary dark:text-golden">
                      Ref: {txn.mpesa_receipt}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
